import React, { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon, TrophyIcon, ArrowUpRightIcon, ArrowDownRightIcon } from 'lucide-react';
import { HistoricalPeriod, getAchievementStatus, getPacingColor } from '../utils/goalData';
interface HistoricalTableProps {
  periods: HistoricalPeriod[];
  timeframe: 'weekly' | 'monthly';
}
type SortField = 'period' | 'softPull' | 'submitted' | 'achievement';
type SortDirection = 'asc' | 'desc';
function StatusBadge({
  percentage
}: {
  percentage: number;
}) {
  const status = getAchievementStatus(percentage);
  const colors = getPacingColor(status);
  const label = percentage >= 105 ? 'Exceeded' : percentage >= 100 ? 'Met' : percentage >= 80 ? 'Missed' : 'Failed';
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${colors.bg} ${colors.text} ${colors.ring}`} data-id="element-58">
      {percentage >= 100 ? <ArrowUpRightIcon className="w-3 h-3" data-id="element-59" /> : <ArrowDownRightIcon className="w-3 h-3" data-id="element-60" />}
      {label}
    </span>;
}
function MetricCell({
  actual,
  goal
}: {
  actual: number;
  goal: number;
}) {
  const pct = goal > 0 ? Math.round(actual / goal * 100) : 0;
  const isOver = actual >= goal;
  return <div className="flex items-center gap-3" data-id="element-61">
      <div className="flex-1" data-id="element-62">
        <div className="flex items-baseline gap-1" data-id="element-63">
          <span className="text-sm font-semibold text-slate-200" data-id="element-64">{actual}</span>
          <span className="text-xs text-slate-500" data-id="element-65">/ {goal}</span>
        </div>
        <div className="mt-1.5 h-1.5 bg-slate-700 rounded-full overflow-hidden w-20" data-id="element-66">
          <div className={`h-full rounded-full transition-all duration-500 ${isOver ? 'bg-emerald-500' : pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
          width: `${Math.min(pct, 100)}%`
        }} data-id="element-67" />
        </div>
      </div>
      <span className={`text-xs font-medium ${isOver ? 'text-emerald-400' : 'text-slate-500'}`} data-id="element-68">
        {pct}%
      </span>
    </div>;
}
export function HistoricalTable({
  periods,
  timeframe
}: HistoricalTableProps) {
  const [sortField, setSortField] = useState<SortField>('period');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }
  function getAchievementPct(period: HistoricalPeriod): number {
    const spPct = period.softPullGoal > 0 ? period.softPullActual / period.softPullGoal : 0;
    const subPct = period.submittedGoal > 0 ? period.submittedActual / period.submittedGoal : 0;
    return Math.round((spPct + subPct) / 2 * 100);
  }
  const sortedPeriods = [...periods].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'period':
        comparison = a.startDate.localeCompare(b.startDate);
        break;
      case 'softPull':
        comparison = a.softPullActual / a.softPullGoal - b.softPullActual / b.softPullGoal;
        break;
      case 'submitted':
        comparison = a.submittedActual / a.submittedGoal - b.submittedActual / b.submittedGoal;
        break;
      case 'achievement':
        comparison = getAchievementPct(a) - getAchievementPct(b);
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const avgAchievement = Math.round(periods.reduce((sum, p) => sum + getAchievementPct(p), 0) / periods.length);
  const goalsMetCount = periods.filter(p => getAchievementPct(p) >= 100).length;
  const bestPeriod = [...periods].sort((a, b) => getAchievementPct(b) - getAchievementPct(a))[0];
  function SortIcon({
    field
  }: {
    field: SortField;
  }) {
    if (sortField !== field) {
      return <ChevronUpIcon className="w-3.5 h-3.5 text-slate-600" data-id="element-69" />;
    }
    return sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5 text-indigo-400" data-id="element-70" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-indigo-400" data-id="element-71" />;
  }
  return <section className="bg-slate-800/80 rounded-2xl border border-slate-700/50 overflow-hidden" style={{
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
  }} aria-label="Historical performance" data-id="element-72">
      <div className="p-6 pb-0" data-id="element-73">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-74">
          <div data-id="element-75">
            <h3 className="text-lg font-semibold text-white" data-id="element-76">
              Historical Performance
            </h3>
            <p className="text-sm text-slate-400 mt-0.5" data-id="element-77">
              Past {timeframe === 'weekly' ? 'weeks' : 'months'} at a glance
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-78">
          <div className="bg-slate-900/60 rounded-xl p-3 text-center border border-slate-700/30" data-id="element-79">
            <p className="text-xs text-slate-500 mb-1" data-id="element-80">Avg Achievement</p>
            <p className={`text-xl font-bold ${avgAchievement >= 100 ? 'text-emerald-400' : 'text-slate-200'}`} data-id="element-81">
              {avgAchievement}%
            </p>
          </div>
          <div className="bg-slate-900/60 rounded-xl p-3 text-center border border-slate-700/30" data-id="element-82">
            <p className="text-xs text-slate-500 mb-1" data-id="element-83">Goals Met</p>
            <p className="text-xl font-bold text-slate-200" data-id="element-84">
              {goalsMetCount}
              <span className="text-sm text-slate-500 font-medium" data-id="element-85">
                /{periods.length}
              </span>
            </p>
          </div>
          <div className="bg-slate-900/60 rounded-xl p-3 text-center border border-slate-700/30" data-id="element-86">
            <p className="text-xs text-slate-500 mb-1" data-id="element-87">Best Period</p>
            <div className="flex items-center justify-center gap-1" data-id="element-88">
              <TrophyIcon className="w-4 h-4 text-amber-400" data-id="element-89" />
              <p className="text-sm font-bold text-slate-200" data-id="element-90">
                {getAchievementPct(bestPeriod)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-91">
        <table className="w-full" role="table" data-id="element-92">
          <thead data-id="element-93">
            <tr className="border-t border-b border-slate-700/50 bg-slate-900/40" data-id="element-94">
              <th className="text-left px-6 py-3" data-id="element-95">
                <button onClick={() => handleSort('period')} className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-300 transition-colors" aria-label="Sort by period" data-id="element-96">
                  Period
                  <SortIcon field="period" data-id="element-97" />
                </button>
              </th>
              <th className="text-left px-6 py-3" data-id="element-98">
                <button onClick={() => handleSort('softPull')} className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-300 transition-colors" aria-label="Sort by soft pull" data-id="element-99">
                  Soft Pull
                  <SortIcon field="softPull" data-id="element-100" />
                </button>
              </th>
              <th className="text-left px-6 py-3" data-id="element-101">
                <button onClick={() => handleSort('submitted')} className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-300 transition-colors" aria-label="Sort by submitted" data-id="element-102">
                  Submitted
                  <SortIcon field="submitted" data-id="element-103" />
                </button>
              </th>
              <th className="text-left px-6 py-3" data-id="element-104">
                <button onClick={() => handleSort('achievement')} className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-300 transition-colors" aria-label="Sort by achievement" data-id="element-105">
                  Achievement
                  <SortIcon field="achievement" data-id="element-106" />
                </button>
              </th>
              <th className="text-left px-6 py-3" data-id="element-107">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-108">
                  Status
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/30" data-id="element-109">
            {sortedPeriods.map(period => {
            const achievementPct = getAchievementPct(period);
            return <tr key={period.id} className="hover:bg-slate-700/20 transition-colors" data-id="element-110">
                  <td className="px-6 py-4" data-id="element-111">
                    <span className="text-sm font-medium text-slate-200" data-id="element-112">
                      {period.label}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-113">
                    <MetricCell actual={period.softPullActual} goal={period.softPullGoal} data-id="element-114" />
                  </td>
                  <td className="px-6 py-4" data-id="element-115">
                    <MetricCell actual={period.submittedActual} goal={period.submittedGoal} data-id="element-116" />
                  </td>
                  <td className="px-6 py-4" data-id="element-117">
                    <span className={`text-sm font-bold ${achievementPct >= 100 ? 'text-emerald-400' : achievementPct >= 80 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-118">
                      {achievementPct}%
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-119">
                    <StatusBadge percentage={achievementPct} data-id="element-120" />
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </section>;
}